import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  type DocumentData,
  type Firestore,
} from 'firebase/firestore'
import type { Auth } from 'firebase/auth'
import type { Recado } from '@domain/recado'
import type { RecadoRepository } from '@domain/ports/finance-repository'
import type { RecordedByKind } from '@domain/product'
import { requireOneOf, requireText, textOr, toIso } from './converters'
import { HOUSEHOLD_ID } from './household-repository'

/**
 * Os recados da casa.
 *
 * ## Ordenado em MEMÓRIA, e por isso sem índice
 *
 * São recados de uma casa de duas pessoas: dezenas, não milhares. Um
 * `orderBy` no servidor pediria uma entrada em `firestore.indexes.json` para
 * economizar uma ordenação de lista curta — e `firestore-indexes.test.ts` varre
 * o que existe, então índice a mais é linha a mais para justificar depois.
 *
 * ## `createdBy` é carimbado AQUI, e não pela tela
 *
 * A mesma disciplina do `comment-repository`: deixá-lo à tela permitiria gravar
 * um recado em nome de outra pessoa. O `autor`, esse sim, vem da tela — ele é a
 * declaração de quem escreveu o conteúdo, e ver `Recado` para a diferença.
 */
function recadosPath(): string {
  return `households/${HOUSEHOLD_ID}/recados`
}

const KINDS = ['user', 'ai', 'other'] as const

function toRecado(id: string, data: DocumentData): Recado {
  const onde = `recados/${id}`
  const autor = (data['autor'] ?? {}) as DocumentData
  return {
    id,
    householdId: textOr(data['householdId'], HOUSEHOLD_ID),
    texto: requireText(data['texto'], `${onde}.texto`),
    autor: {
      kind: requireOneOf<RecordedByKind>(autor['kind'], KINDS, `${onde}.autor.kind`),
      id: typeof autor['id'] === 'string' ? autor['id'] : null,
      label: textOr(autor['label'], 'alguém da casa'),
    },
    createdBy: textOr(data['createdBy'], ''),
    // `serverTimestamp()` chega `null` no snapshot local, antes de o servidor
    // responder — e a lista redesenha no instante da gravação. Vazio, como nos
    // outros adapters: o relógio do host não entra no app, e quem ordena sabe
    // que vazio quer dizer "acabou de nascer".
    createdAt: toIso(data['createdAt']) ?? '',
  }
}

export function createFirebaseRecadoRepository(db: Firestore, auth: Auth): RecadoRepository {
  const uid = (): string => {
    const atual = auth.currentUser?.uid
    if (atual === undefined) {
      throw new Error('recado-repository: não há sessão — nenhum recado pode ser atribuído.')
    }
    return atual
  }

  return {
    list: async () => {
      const snap = await getDocs(collection(db, recadosPath()))
      return snap.docs
        .map((d) => toRecado(d.id, d.data()))
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    },

    create: async (novo) => {
      const criado = await addDoc(collection(db, recadosPath()), {
        householdId: HOUSEHOLD_ID,
        texto: novo.texto.trim(),
        autor: { kind: novo.autor.kind, id: novo.autor.id, label: novo.autor.label },
        createdBy: uid(),
        createdAt: serverTimestamp(),
      })
      return criado.id
    },

    remove: async (id) => {
      await deleteDoc(doc(db, recadosPath(), id))
    },
  }
}
